import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Modal from "./Modal";
import "./CustomersTab.css";

export default function CustomersTab({ setIsAnyModalActive }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState(null);
  const [profileOrders, setProfileOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const res = await axios.get("https://admin.deedelicious.com/php/get-customers.php");
        setCustomers(res.data.customers || []);
      } catch (err) {
        console.error("Failed to fetch customers:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

   const handleViewProfile = async (customerPhone) => {
    try {
      setOrdersLoading(true);

      // ✅ changed: fetch by phone, not customer_id
      const res = await axios.get(
        `https://admin.deedelicious.com/php/get-customer-orders.php?customer_phone=${encodeURIComponent(
          customerPhone
        )}`
      );

      setProfileOrders(res.data.orders || []);

      const customer = customers.find((c) => c.customer_phone === customerPhone);
      setProfileCustomer(customer || { customer_phone: customerPhone });

      setShowProfileModal(true);
      if (setIsAnyModalActive) setIsAnyModalActive(true);
    } catch (err) {
      console.error("Failed to fetch customer orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowProfileModal(false);
    setProfileCustomer(null);
    setProfileOrders([]);
    setExpandedOrderId(null);
    if (setIsAnyModalActive) setIsAnyModalActive(false);
  };

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const summary = useMemo(() => {
    if (!profileOrders.length) return { totalOrders: 0, lastOrderDate: "-", totalSpent: 0 };

    const totalOrders = profileOrders.length;
    const lastOrderDate = new Date(profileOrders[0].created_at).toLocaleDateString("en-GB");
    const totalSpent = profileOrders.reduce(
      (sum, o) => sum + parseFloat(o.total || 0),
      0
    );

    return { totalOrders, lastOrderDate, totalSpent };
  }, [profileOrders]);

  const { totalOrders, lastOrderDate, totalSpent } = summary;

  return (
    <div className="customers-tab">
      <h1>Customer Data</h1>

      {loading ? (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="customers-table-wrapper">
          <table className="customers-table">
            <thead>
              <tr>
                <th>S/N</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Last Order</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer, index) => {
                const name = customer.customer_name || "N/A";
                const email = customer.customer_email || "N/A";
                const phone = customer.customer_phone || "";

                return (
                  <tr key={phone || `${name}_${index}`}>
                    <td>{index + 1}</td>
                    <td>{name}</td>
                    <td>{email}</td>
                    <td>{phone || "N/A"}</td>
                    <td>
                      {customer.last_order_date
                        ? new Date(customer.last_order_date).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="action-buttons">
                      <button
                        className="call-btn"
                        disabled={!phone}
                        onClick={() => (window.location.href = `tel:${phone}`)}
                      >
                        Call
                      </button>

                      <button
                        className="view-profile-btn"
                        disabled={!phone}
                        onClick={() => handleViewProfile(phone)}
                      >
                        View Profile
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showProfileModal && profileCustomer && (
        <Modal
          title={`Profile: ${profileCustomer.customer_name || "Customer"}`}
          onClose={handleCloseModal}
          showCancel={false}
          className="profile-modal"
          showSubmitButton={false}
        >
          {/* Customer summary */}
          <div className="profile-summary">
            <p>
              <strong>Phone:</strong> {profileCustomer.customer_phone || "N/A"}
            </p>
            {profileCustomer.customer_email && (
              <p>
                <strong>Email:</strong> {profileCustomer.customer_email}
              </p>
            )}
            <p>
              <strong>Total Orders:</strong> {totalOrders}
            </p>
            <p>
              <strong>Most Recent Order:</strong> {lastOrderDate}
            </p>
            <p>
              <strong>Total Spent:</strong> £{totalSpent.toFixed(2)}
            </p>
          </div>

          {ordersLoading ? (
            <p>Loading orders...</p>
          ) : profileOrders.length === 0 ? (
            <p className="no-orders">No orders yet.</p>
          ) : (
            <ul className="customer-orders-list">
              {profileOrders.map((order) => (
                <li
                  key={order.id}
                  className="customer-order-card"
                  onClick={() => toggleOrder(order.id)}
                >
                  <div className="order-date">
                    <span className="order-date-text">
                      {new Date(order.created_at).toLocaleDateString("en-GB")}
                    </span>
                    <span className="order-id">Ref: {order.id}</span>
                  </div>

                  <div className="customer-order-header">
                    <strong>Order #{order.order_number}</strong>{" "}
                    <span>{order.order_type}</span>
                  </div>

                  <p className="customer-order-total">
                    <strong>£{parseFloat(order.total).toFixed(2)}</strong>
                  </p>

                  {expandedOrderId === order.id && (
                    <div className="profile-order-items expanded-background">
                      {order.items.map((item) => (
                        <div key={item.id} className="profile-order-item">
                          <div className="profile-order-item-header">
                            <span className="profile-order-item-title">
                              {item.name}{" "}
                              {parseFloat(item.base_price || 0) > 0 &&
                                ` (£${parseFloat(item.base_price).toFixed(2)})`}
                            </span>
                          </div>

                          {item.required_option_name && (
                            <p className="profile-order-item-option">
                              {item.required_option_name}
                              {parseFloat(item.required_option_price || 0) > 0 &&
                                ` (£${parseFloat(item.required_option_price).toFixed(2)})`}
                            </p>
                          )}

                          {item.selectedOptionalOptions?.length > 0 && (
                            <div className="profile-order-item-option">
                              <p>
                                <strong>Optional:</strong>
                              </p>
                              <ul>
                                {item.selectedOptionalOptions.map((opt, idx) => (
                                  <li key={idx}>
                                    {opt.name} x {opt.quantity}
                                    {parseFloat(opt.price) > 0 &&
                                      ` (£${parseFloat(opt.price).toFixed(2)})`}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <div className="profile-order-item-total-wrapper">
                            <span className="profile-order-item-total-text">
                              x {item.quantity} = £{parseFloat(item.total_price).toFixed(2)}
                            </span>
                          </div>

                          <hr className="profile-order-item-divider" />
                        </div>
                      ))}

                      {order.service_charge != null && order.service_charge > 0 && (
                        <p className="profile-order-notes">
                          <strong>Service Charge:</strong> £
                          {parseFloat(order.service_charge).toFixed(2)}
                        </p>
                      )}

                      {order.notes?.trim() && (
                        <p className="profile-order-notes">
                          <strong>Notes:</strong> {order.notes}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
}
